import type { Message } from '../../types';
import { staticAnalysisMarkdown } from './staticAnalysisMarkdown';
import { PlcBrand, type PlcTargetProfile, type PLCTestCase } from '@realplc/types';
import { getWorkspaceMessageCode, getWorkspaceValidationReport } from '../workspaceViewModel';
import { isBeckhoffBrand, isSiemensBrand } from './plcTarget';
import { extractPLCBlocks, getMainBlockName } from '../extractPLCBlocks';
import type { ArtifactFile, BuildTurnPresentationOptions, TurnKind } from './presentationTypes';
import {
  buildDeliverableName,
  formatProgramFile,
  formatSiblingFile,
  type DeliverableName,
} from './deliverableName';

function resolveTargetProfile(message: Message, configured?: PlcTargetProfile): PlcTargetProfile {
  if (configured) return configured;
  const pipeline = message.pipeline as unknown as Record<string, unknown> | undefined;
  const requirement = pipeline?.requirement && typeof pipeline.requirement === 'object'
    ? pipeline.requirement as Record<string, unknown>
    : undefined;
  const vendor = String(requirement?.brand || requirement?.vendor || '').toLowerCase();
  // `plcTarget` owns this question; see it for why an unstated brand is not
  // Siemens.
  const brand = isSiemensBrand(vendor) ? PlcBrand.Siemens
    : isBeckhoffBrand(vendor) ? PlcBrand.Beckhoff
      : PlcBrand.Codesys;
  return { brand, programmingModel: 'classic_plc', sourceEncoding: 'utf8_bom' };
}

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * 名字算一次，各处只做格式化。调用方（MessageBubble）已经算好时传对象进来，保证屏幕上
 * 的下载名、这里派生的兄弟文件名、交付包名同源；测试等未传的场合就地补算。
 *
 * 纯函数：同样的 message + options 必然得到同样的名字，重复调用无副作用。
 */
/**
 * The one program the turn renders in full, inline, with the code in it.
 *
 * Shared so the two surfaces cannot disagree about which artifact that is:
 * `ProgramSection` draws this one, and the closing block leaves it out of its
 * file groups. They used to pick independently, which listed the same file
 * under two names on the same screen once the closing block became universal.
 *
 * The unified "complete program" is preferred over the individual blocks, so
 * the conversation shows one program rather than five.
 */
export function findMainProgramArtifact(
  artifacts: ArtifactFile[],
): ArtifactFile | undefined {
  const programs = artifacts.filter(artifact => artifact.kind === 'plc_program');
  return programs.find(artifact => artifact.id.endsWith('-full')) || programs[0];
}

export function resolveDeliverableName(
  message: Message,
  primaryText: string,
  options: BuildTurnPresentationOptions,
): DeliverableName {
  if (options.deliverableName) return options.deliverableName;
  const validationReport = getWorkspaceValidationReport(message) as Record<string, unknown> | null;
  return buildDeliverableName({
    requirement: message.pipeline?.requirement,
    summary: primaryText,
    codeVersionLabel: options.codeVersionLabel,
    createdAt: message.timestamp ?? Date.now(),
    validated: validationReport?.valid !== false,
    code: getWorkspaceMessageCode(message) || (message.parsed as any)?.code,
  });
}

function compactText(value: unknown, max = 500): string {
  if (!hasText(value)) return '';
  const cleaned = value
    .replace(/<think>[\s\S]*?(?:<\/think>|$)/gi, '')
    .replace(/```mermaid[\s\S]*?```/gi, '')
    .replace(/```(?:json)?[\s\S]*?```/gi, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (cleaned.length <= max) return cleaned;
  return `${cleaned.slice(0, max).trimEnd()}…`;
}

/**
 * The commissioning cases, written into the document an engineer takes to site.
 *
 * They used to live only in the turn, expanded inline between the preflight's
 * root causes and the repair rounds — a block that interrupted the one narrative
 * the reader was following, and that left with the conversation. A test case is
 * a deliverable: it is read at the machine, not in the transcript.
 */
function renderTestCases(testCases: PLCTestCase[] | undefined, isChinese: boolean): string[] {
  if (!testCases?.length) return [];

  const lines: string[] = ['', `## ${isChinese ? '测试用例' : 'Test cases'}`, ''];

  for (const testCase of testCases) {
    lines.push(`### ${testCase.title}`, '');

    if (testCase.preconditions?.length) {
      lines.push(`**${isChinese ? '前置条件' : 'Preconditions'}**`, '');
      lines.push(...testCase.preconditions.map(item => `- ${item}`), '');
    }
    if (testCase.steps?.length) {
      lines.push(`**${isChinese ? '步骤' : 'Steps'}**`, '');
      lines.push(...testCase.steps.map(item => `- ${item}`), '');
    }
    if (testCase.expected?.length) {
      lines.push(`**${isChinese ? '预期' : 'Expected'}**`, '');
      lines.push(...testCase.expected.map(item => `- ${item}`), '');
    }
  }

  return lines;
}

function buildImplementationSummary(
  primaryText: string,
  isChinese: boolean,
  options: {
    mermaidContent?: string;
    validationFailed: boolean;
    validationSummary?: string;
    testCases?: PLCTestCase[];
  },
): string {
  if (!hasText(primaryText)) return '';
  const lines = [
    `# ${isChinese ? 'RealPLC 总结报告' : 'RealPLC Summary Report'}`,
    '',
    `> ${isChinese ? '结果状态' : 'Result status'}：${options.validationFailed
      ? (isChinese ? '未通过校验，仅供工程审查' : 'Validation not passed; for engineering review only')
      : (isChinese ? '已生成' : 'Generated')}`,
    '',
    `## ${isChinese ? '结果摘要' : 'Summary'}`,
    '',
    primaryText.trim(),
  ];

  if (hasText(options.mermaidContent)) {
    lines.push(
      '',
      `## ${isChinese ? '控制逻辑流程图' : 'Control Logic Flowchart'}`,
      '',
      ...(options.validationFailed
        ? [`> ${isChinese ? '本流程图基于未通过校验的生成版本，不代表可直接部署。' : 'This flowchart is based on an unvalidated generated version and is not deployment-ready.'}`, '']
        : []),
      '```mermaid',
      options.mermaidContent.trim(),
      '```',
    );
  }

  if (hasText(options.validationSummary)) {
    lines.push(
      '',
      `## ${isChinese ? '校验结果' : 'Validation'}`,
      '',
      options.validationSummary.trim(),
    );
  }

  lines.push(...renderTestCases(options.testCases, isChinese));

  return lines.join('\n');
}

function appendUnvalidatedSuffix(fileName: string): string {
  const match = fileName.match(/^(.*?)(\.[^.]+)?$/);
  const stem = match?.[1] || fileName;
  const extension = match?.[2] || '';
  return `${stem}_UNVALIDATED${extension}`;
}

function buildValidationMarkdown(message: Message, isChinese: boolean): string {
  const report = getWorkspaceValidationReport(message) as Record<string, unknown> | null;
  if (!report) return '';

  const lines = [`# ${isChinese ? '安全与质量校验报告' : 'Safety and Quality Validation'}`, ''];
  if (hasText(report.summary)) lines.push(report.summary, '');
  const result = report.valid === false
    ? (isChinese ? '未通过' : 'Not passed')
    : (isChinese ? '通过' : 'Passed');
  lines.push(`- ${isChinese ? '最终结果' : 'Final result'}：${result}`);

  const issues = Array.isArray(report.issues) ? report.issues : [];
  if (issues.length > 0) {
    lines.push('', `## ${isChinese ? '问题' : 'Issues'}`, '');
    for (const issue of issues.slice(0, 100)) {
      if (!issue || typeof issue !== 'object') continue;
      const record = issue as Record<string, unknown>;
      const messageText = compactText(record.message || record.description || record.title);
      if (messageText) lines.push(`- ${messageText}`);
    }
  }
  const supplemental = staticAnalysisMarkdown(report.staticAnalysis, isChinese);
  if (supplemental) lines.push('', supplemental);
  return lines.join('\n').trim();
}

export function buildArtifacts(
  message: Message,
  kind: TurnKind,
  primaryText: string,
  options: BuildTurnPresentationOptions,
  mermaidContent?: string,
): ArtifactFile[] {
  if (kind !== 'completed' && kind !== 'recovery') return [];

  const artifacts: ArtifactFile[] = [];
  const isChinese = options.language !== 'English';
  const code = getWorkspaceMessageCode(message) || (message.parsed as any)?.code || '';
  const versionLabel = options.codeVersionLabel || 'V1.0';
  const validationReport = getWorkspaceValidationReport(message) as Record<string, unknown> | null;
  const validationFailed = validationReport?.valid === false;
  const programStatus: ArtifactFile['status'] = validationFailed ? 'unvalidated' : 'ready';

  // 校验状态以 message 的报告为准：调用方传进来的名字对象只提供项目名/日期/版本/扩展名。
  // 两边各存一份 validated 就会出现文件名说通过、状态标记说未通过的分裂。
  const deliverableName: DeliverableName = {
    ...resolveDeliverableName(message, primaryText, options),
    validated: !validationFailed,
  };
  const deliveryFileName = formatProgramFile(deliverableName);

  if (hasText(code)) {
    // Split into individual blocks (FBs, FCs, PRG, etc.)
    const blocks = extractPLCBlocks(code, resolveTargetProfile(message, options.targetProfile));

    // `<= 1`, not `=== 0`: extractPLCBlocks returns one entry for a single-block
    // program, so an `=== 0` guard sent it down the split path and renamed the
    // download from the name shown on screen (`Motor_PLC.scl`) to a derived one
    // in a subfolder (`ProgramBlocks/FB_Motor.scl`). Splitting only earns its
    // keep when there is more than one object to import.
    if (blocks.length <= 1) {
      // Single block or unparseable — one unified artifact
      artifacts.push({
        id: `${message.id}-plc-program`,
        kind: 'plc_program',
        title: isChinese ? 'PLC 程序' : 'PLC program',
        fileName: deliveryFileName,
        mimeType: 'text/plain',
        extension: deliverableName.extension,
        versionLabel,
        status: programStatus,
        sourceMessageId: message.id,
        previewTarget: 'code',
        downloadable: true,
        content: code,
      });
    } else {
      // Multiple blocks — each gets its own downloadable artifact
      const mainName = getMainBlockName(blocks);
      for (const block of blocks) {
        const isMain = block.name === mainName;
        artifacts.push({
          id: `${message.id}-block-${block.kind}-${block.name}`,
          kind: 'plc_program',
          title: isMain
            ? (isChinese ? `主程序 · ${block.name}` : `Main · ${block.name}`)
            : block.name,
          fileName: validationFailed ? appendUnvalidatedSuffix(block.fileName) : block.fileName,
          mimeType: 'text/plain',
          extension: block.descriptor.extension,
          versionLabel,
          status: programStatus,
          sourceMessageId: message.id,
          previewTarget: 'code',
          downloadable: true,
          programBlock: true,
          content: block.code,
        });
      }

      // Also include the full unified file for completeness. It is what the
      // conversation shows and what the turn is named after, so it has to be in
      // the list; `packaged: false` is what keeps it out of the ZIP.
      artifacts.push({
        id: `${message.id}-plc-program-full`,
        kind: 'plc_program',
        title: isChinese ? '完整程序（全部）' : 'Complete program (all)',
        fileName: deliveryFileName,
        mimeType: 'text/plain',
        extension: deliverableName.extension,
        versionLabel,
        status: programStatus,
        sourceMessageId: message.id,
        previewTarget: 'code',
        downloadable: true,
        packaged: false,
        content: code,
      });
    }
  }

  const summaryMarkdown = buildImplementationSummary(primaryText, isChinese, {
    mermaidContent,
    validationFailed,
    validationSummary: hasText(validationReport?.summary) ? validationReport.summary : undefined,
    testCases: message.pipeline?.testCases,
  });
  if (options.includeMarkdownArtifacts !== false && summaryMarkdown) {
    artifacts.push({
      id: `${message.id}-implementation-summary`,
      kind: 'implementation_summary',
      title: isChinese ? '实现说明' : 'Implementation summary',
      fileName: formatSiblingFile(deliverableName, 'ImplementationSummary', '.md'),
      mimeType: 'text/markdown;charset=utf-8',
      extension: '.md',
      versionLabel,
      status: validationFailed ? 'unvalidated' : 'ready',
      sourceMessageId: message.id,
      previewTarget: 'markdown',
      downloadable: true,
      content: summaryMarkdown,
    });
  }

  const validationMarkdown = buildValidationMarkdown(message, isChinese);
  if (options.includeMarkdownArtifacts !== false && validationMarkdown) {
    artifacts.push({
      id: `${message.id}-validation-report`,
      kind: 'validation',
      title: isChinese ? '安全校验报告' : 'Safety validation report',
      fileName: formatSiblingFile(deliverableName, 'SafetyValidation', '.md'),
      mimeType: 'text/markdown;charset=utf-8',
      extension: '.md',
      versionLabel,
      // 这份报告的状态跟着裁决走，不能永远写死 ready。
      //
      // 2026-09-10 那一轮：程序标「已生成 · 未通过校验」，紧挨着的安全校验报告
      // 标「已就绪」。同一屏里两个相反的结论，读到的人只会相信后者——「安全校验
      // 已就绪」听起来就是校验过了。报告本身确实生成完了，但它记录的是一次失败，
      // 行上的状态说的是这次交付的裁决，不是文件写没写出来。
      status: validationFailed ? 'unvalidated' : 'ready',
      sourceMessageId: message.id,
      previewTarget: 'markdown',
      downloadable: true,
      content: validationMarkdown,
    });
  }

  // The combined source stays in the list — the inline code card, the file list
  // and the closing summary all look it up by id, and dropping it here is what
  // made them fall back to whichever object happened to be declared first (an
  // 8-line ENUM, for a CODESYS program). It carries `packaged: false` instead,
  // so it is still kept out of the main ZIP where TIA would import duplicates.
  return artifacts;
}
